import React, { Fragment, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cell } from "react-table";
import MockTableCell from "./MockTableCell";
import { DataWithUUID, MockTableRowProps } from "../types";

const TableRow = <T extends DataWithUUID>({
	row,
	prepareRow,
	activeIndex,
	activeRowColor = "bg-pink-100",
	rowHoverClasses = "hover:bg-navy-100 hover:cursor-pointer",
	rowClassNames = "",
	hasInfiniteScroll,
	rowUuid,
	onRowClick,
	expandedCells,
	onExpandCell,
	hasDropDown = false,
	onFetchRowData,
	loadingIndicator,
	errorIndicator,
	expandedContent,
	expandedRowUuid = null,
	cellExpandable = false,
	onToggleRow,
	maxCharacters = 30,
	cellMaxWidthWhenExpanded = 50,
	additionalCellClassNames = "",
	toggleComponent = undefined,
	linkTextClassNames = "",
	cellTextClassNames = "",
	onRowOverflow = () => {},
	expandedCellActionButton,
	expandedCellAlwaysVisibleActionButton,
	onMouseEnter,
	onMouseLeave,
	hoverIconStyles,
	hasTableRowExpansion,
	displayStickyColumn,
}: MockTableRowProps<T>) => {
	prepareRow(row);
	const rowClasses = `border-primary ${
		activeIndex === row.index ? ` activeRow ${activeRowColor}` : ""
	}`;
	const [overflowMap, setOverflowMap] = useState<Record<string, boolean>>({});

	/* keep fetch/loading local */
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	/* determine if *this* row is open */
	const isTableRowExpanded = hasDropDown && rowUuid === expandedRowUuid;

	const handleRowClick = async (
		e: React.MouseEvent<HTMLTableRowElement, MouseEvent>
	) => {
		onRowClick(row.index, rowUuid ?? "", e);

		if (hasDropDown && rowUuid && onToggleRow) {
			onToggleRow(rowUuid);

			if (!isTableRowExpanded && onFetchRowData) {
				try {
					setIsLoading(true);
					setError(null);
					await onFetchRowData(rowUuid);
				} catch (err) {
					if (err instanceof Error) setError(err);
				} finally {
					setIsLoading(false);
				}
			}
		}
	};

	const handleOverflow = useCallback(
		(cellKey: string, isOverflowing: boolean) => {
			setOverflowMap((m) => {
				const next = { ...m, [cellKey]: isOverflowing };
				const anyOverflow = Object.values(next).some(Boolean);
				// notify parent of overall row overflow
				onRowOverflow?.(rowUuid as string, anyOverflow);
				return next;
			});
		},
		[onRowOverflow, rowUuid]
	);

	// Fix: Key is discarded from the spread obj, react still gets key just not from spread i.e. key={rowUuid}

	// Pull props ahead of time and separate out 'key'
	// If row.getRowProps exists, call function, else use empty
	// _ignoredKey extracts the key prop and stores it, it will be discarded
	// ...safeRowProps gathers remaining props except key into obj using '...'
	const { key: _ignoredKey, ...safeRowProps } = row.getRowProps
		? row.getRowProps()
		: {};

	const hoverButtonFn =
		typeof expandedCellActionButton === "function"
			? expandedCellActionButton
			: (_fullText: string, _rowIndex: number) =>
					expandedCellActionButton;

	// 2) always-visible button => ensure it's a function(fullText,rowIndex)
	const alwaysVisibleFn =
		typeof expandedCellAlwaysVisibleActionButton === "function"
			? expandedCellAlwaysVisibleActionButton
			: (_fullText: string, _rowIndex: number) =>
					expandedCellAlwaysVisibleActionButton;

	return (
		<Fragment>
			<tr
				data-testid="table-row"
				key={rowUuid}
				className={`peer border-b border-b-navy-200  ${rowHoverClasses} ${rowClasses} ${rowClassNames}`}
				onClick={handleRowClick}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				{...safeRowProps}
			>
				{row.cells.map((cell: Cell<T>, idx: number) => {
					const cellKey = `${rowUuid ?? "no-uuid"}-${cell.column.id}`;

					const isFirstCell = idx === 0;
					const isLastCell = idx === row.cells.length - 1;
					const isRightSideStickyColumn =
						displayStickyColumn && isLastCell;

					return (
						<Fragment key={`${cellKey}-${idx}`}>
							<MockTableCell
								key={`${cellKey}-${idx}`}
								cell={cell}
								isLastCell={isLastCell}
								hasInfiniteScroll={hasInfiniteScroll}
								hasCellExpansion={cellExpandable}
								isCellExpanded={expandedCells[cellKey] ?? false}
								onExpandCell={() => onExpandCell(cellKey)}
								linkTextClassNames={linkTextClassNames}
								maxCharacters={maxCharacters}
								cellMaxWidthWhenExpanded={
									cellMaxWidthWhenExpanded
								}
								additionalCellClassNames={
									additionalCellClassNames
								}
								toggleComponent={toggleComponent}
								cellTextClassNames={cellTextClassNames}
								onCellOverflow={(isOverflowing) =>
									handleOverflow(cellKey, isOverflowing)
								}
								expandedCellAlwaysVisibleActionButton={
									alwaysVisibleFn
								}
								expandedCellActionButton={hoverButtonFn}
								hoverIconStyles={hoverIconStyles}
								showExpandRowChevronButton={
									hasTableRowExpansion && isFirstCell
								}
								isTableRowExpanded={isTableRowExpanded}
								cellIndex={idx}
								isRightSideStickyColumn={
									isRightSideStickyColumn
								}
							/>
						</Fragment>
					);
				})}
			</tr>

			{hasDropDown && (
				<AnimatePresence>
					{isTableRowExpanded && (
						<tr data-testid="expanded-row">
							<td colSpan={row.cells.length} className="p-0">
								<motion.div
									key="expanded-dropdown-content"
									initial={{ height: 0, opacity: 0 }}
									animate={{ height: "auto", opacity: 1 }}
									exit={{ height: 0, opacity: 0 }}
									transition={{ duration: 0.3 }}
									className="overflow-hidden w-full"
								>
									{isLoading ? (
										loadingIndicator ?? (
											<span>Loading...</span>
										)
									) : error ? (
										errorIndicator ? (
											errorIndicator(error)
										) : (
											<span className="text-red-700">
												Error: {error.message}
											</span>
										)
									) : (
										expandedContent ?? (
											<span>drop down – {rowUuid}</span>
										)
									)}
								</motion.div>
							</td>
						</tr>
					)}
				</AnimatePresence>
			)}
		</Fragment>
	);
};

export default TableRow;
